import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Flex, Heading, Text, Button, SimpleGrid, VStack, HStack, 
  Badge, useColorModeValue, Spinner, Icon, Progress, Alert, AlertIcon
} from '@chakra-ui/react';
import { useFeatureFlag, isBetaUser } from '../utils/featureFlags';
import { analyzeImageContent, categorizeTemplatesForContent } from '../utils/contentAnalysis';
import supabase from '../utils/supabaseUtils';

// Content type selector component
const ContentTypeSelector = ({ selectedType, onTypeChange, disabled = false, analysisData = null }) => {
  const designTypes = [
    { 
      id: 'image', 
      label: 'Photo Design', 
      icon: '📸',
      description: 'Player cards, team photos, action shots',
      isDefault: true
    },
    { 
      id: 'type', 
      label: 'Text Design', 
      icon: '📝',
      description: 'Quote cards, announcements, stats'
    },
    { 
      id: 'video', 
      label: 'Video Design', 
      icon: '🎬',
      description: 'Highlight reels, motion graphics',
      comingSoon: true
    }
  ];

  return (
    <Box mb={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md" color="gray.800">Choose Design Type</Heading>
        {analysisData && (
          <Badge colorScheme="green" variant="outline">
            AI Analyzed ✨
          </Badge>
        )}
      </Flex>
      
      {/* Show AI insights if available */}
      {analysisData && (
        <Box mb={4} p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
          <Text fontSize="sm" color="blue.700">
            <strong>AI Analysis:</strong> {analysisData.recommendations?.[0]?.reason || 'Content analyzed for optimal design suggestions'}
          </Text>
        </Box>
      )}
      
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {designTypes.map(type => {
          // Highlight recommended type based on analysis
          const isRecommended = analysisData?.contentType === type.id;
          
          return (
            <Button
              key={type.id}
              onClick={() => !disabled && !type.comingSoon && onTypeChange(type.id)}
              variant={selectedType === type.id ? 'solid' : 'outline'}
              colorScheme={selectedType === type.id ? 'blue' : isRecommended ? 'green' : 'gray'}
              h="auto"
              p={4}
              flexDirection="column"
              isDisabled={disabled || type.comingSoon}
              position="relative"
              border={isRecommended ? '2px solid' : '1px solid'}
              borderColor={isRecommended ? 'green.300' : undefined}
            >
              {type.comingSoon && (
                <Badge 
                  position="absolute" 
                  top={2} 
                  right={2} 
                  colorScheme="orange" 
                  variant="solid"
                  fontSize="xs"
                >
                  Soon
                </Badge>
              )}
              {isRecommended && (
                <Badge 
                  position="absolute" 
                  top={2} 
                  left={2} 
                  colorScheme="green" 
                  variant="solid"
                  fontSize="xs"
                >
                  AI Pick
                </Badge>
              )}
              <Text fontSize="24px" mb={2}>{type.icon}</Text>
              <Text fontWeight="semibold" mb={1}>{type.label}</Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                {type.description}
              </Text>
            </Button>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

// Analysis insights component
const AnalysisInsights = ({ analysis, isVisible = true }) => {
  if (!isVisible || !analysis) return null;

  return (
    <Box mb={6} p={4} bg="gradient" bgGradient="linear(135deg, blue.50, purple.50)" 
         borderRadius="xl" border="1px" borderColor="blue.200">
      <Flex align="center" mb={3}>
        <Text fontSize="20px" mr={2}>🧠</Text>
        <Heading size="sm" color="gray.800">AI Content Analysis</Heading>
      </Flex>
      
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} fontSize="sm">
        <VStack align="start" spacing={2}>
          <Text><strong>Content Type:</strong> {analysis.contentType === 'image' ? 'Photo-based' : 'Text-based'}</Text>
          <Text><strong>Layout:</strong> {analysis.suggestedLayout?.replace('_', ' ') || 'Standard'}</Text>
          <Text><strong>Orientation:</strong> {
            analysis.characteristics?.isPortrait ? 'Portrait' : 
            analysis.characteristics?.isLandscape ? 'Landscape' : 'Square'
          }</Text>
        </VStack>
        <VStack align="start" spacing={2}>
          <Text><strong>People Count:</strong> {analysis.characteristics?.peopleCount || 1}</Text>
          <Text><strong>Brightness:</strong> {analysis.characteristics?.brightness || 'Medium'}</Text>
          <Text><strong>Sport:</strong> {analysis.sport?.primary || 'Baseball'}</Text>
        </VStack>
      </SimpleGrid>
      
      <Box mt={3}>
        <Text fontSize="xs" color="gray.600">
          Confidence: {Math.round((analysis.confidence?.contentType || 0.5) * 100)}%
        </Text>
      </Box>
    </Box>
  );
};

// Main Smart Design Selector component
const SmartDesignSelector = ({ uploadedImageURL, user }) => {
  const [selectedType, setSelectedType] = useState('image');
  const [generatedDesigns, setGeneratedDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const navigate = useNavigate();

  // Feature flags
  const dynamicGenerationEnabled = useFeatureFlag('DYNAMIC_DESIGN_GENERATION');
  const contentTypeSelectorEnabled = useFeatureFlag('CONTENT_TYPE_SELECTOR');
  const aiContentAnalysisEnabled = useFeatureFlag('AI_CONTENT_ANALYSIS');
  const isUserBeta = user?.email ? isBetaUser(user.email) : false;

  // For now, use existing templates but filter/organize them by type
  const [templates, setTemplates] = useState([]);

  // Analyze uploaded image when component mounts
  useEffect(() => {
    if (uploadedImageURL && (aiContentAnalysisEnabled || isUserBeta)) {
      analyzeUploadedContent();
    }
  }, [uploadedImageURL, aiContentAnalysisEnabled, isUserBeta]);

  // Fetch templates when type changes
  useEffect(() => {
    fetchTemplatesForType(selectedType);
  }, [selectedType, analysisData]);

  const analyzeUploadedContent = async () => {
    if (!uploadedImageURL) return;
    
    setAnalysisLoading(true);
    console.log('Starting content analysis for:', uploadedImageURL);
    
    try {
      const result = await analyzeImageContent(uploadedImageURL);
      
      if (result.success) {
        setAnalysisData(result.analysis);
        
        // Auto-select the recommended content type
        if (result.analysis.contentType) {
          setSelectedType(result.analysis.contentType);
        }
        
        console.log('Content analysis complete:', result.analysis);
      } else {
        console.warn('Content analysis failed:', result.error);
        // Use fallback analysis
        setAnalysisData(result.analysis);
      }
    } catch (error) {
      console.error('Error during content analysis:', error);
      setError('Failed to analyze content, using default suggestions');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const fetchTemplatesForType = async (type) => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch existing templates
      let query = supabase
        .from('templates')
        .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
        .eq('is_public', true)
        .eq('style_subcategory', 'Baseball/Softball');
      
      const { data, error } = await query.order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      setTemplates(data || []);
      
      // Use smart categorization if analysis is available
      let organizedTemplates;
      if (analysisData && (dynamicGenerationEnabled || isUserBeta)) {
        organizedTemplates = categorizeTemplatesForContent(data || [], analysisData);
        console.log('Smart template categorization applied:', organizedTemplates);
      } else {
        organizedTemplates = organizeTemplatesByType(data || [], type);
      }
      
      setGeneratedDesigns(organizedTemplates);
      
    } catch (err) {
      console.error('Error fetching templates for type:', err);
      setError('Failed to load designs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Organize existing templates by design type (fallback method)
  const organizeTemplatesByType = (templates, type) => {
    return templates.map(template => ({
      ...template,
      designType: type,
      isGenerated: false,
      smartScore: 0.5,
      matchReason: 'Standard template suitable for most content'
    }));
  };

  const handleDesignSelect = (design) => {
    // Use existing customization flow for now
    navigate(`/customize/${design.id}`, { 
      state: { 
        uploadedImageURL,
        designType: selectedType,
        analysisData: analysisData
      } 
    });
  };

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
    
    // Track AI recommendation accuracy
    if (analysisData) {
      const wasRecommended = analysisData.contentType === newType;
      console.log(`User ${wasRecommended ? 'followed' : 'overrode'} AI recommendation`);
    }
  };

  // Show beta badge if user has access to new features
  const showBetaBadge = isUserBeta || dynamicGenerationEnabled || aiContentAnalysisEnabled;

  return (
    <Box>
      {showBetaBadge && (
        <Box mb={4} p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
          <HStack>
            <Badge colorScheme="blue" variant="solid">BETA</Badge>
            <Text fontSize="sm" color="blue.700">
              You have early access to Smart Design Generation! 
              {analysisData ? ' Your content has been analyzed by AI.' : ' Upload an image for AI analysis.'}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Analysis Loading State */}
      {analysisLoading && (
        <Box mb={6} p={4} bg="yellow.50" borderRadius="md" border="1px" borderColor="yellow.200">
          <HStack spacing={3}>
            <Spinner size="sm" color="yellow.500" />
            <VStack align="start" spacing={1} flex="1">
              <Text fontSize="sm" fontWeight="medium" color="yellow.800">
                AI is analyzing your content...
              </Text>
              <Progress size="sm" isIndeterminate colorScheme="yellow" />
            </VStack>
          </HStack>
        </Box>
      )}

      {/* Analysis Insights */}
      <AnalysisInsights 
        analysis={analysisData} 
        isVisible={!analysisLoading && (aiContentAnalysisEnabled || isUserBeta)} 
      />

      {/* Content Type Selector - enhanced with AI recommendations */}
      {(contentTypeSelectorEnabled || isUserBeta) && (
        <ContentTypeSelector 
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          disabled={loading}
          analysisData={analysisData}
        />
      )}

      {/* Design Results */}
      <Box>
        <Flex align="center" justify="space-between" mb={4}>
          <Box>
            <Heading size="lg" color="gray.800">
              {selectedType === 'image' ? 'Photo Designs' : 
               selectedType === 'type' ? 'Text Designs' : 'Video Designs'}
              {analysisData && ' - AI Optimized'}
            </Heading>
            <Text color="gray.600">
              {analysisData ? 
                `Smart suggestions based on your ${analysisData.characteristics?.isPortrait ? 'portrait' : 'landscape'} ${analysisData.sport?.primary || 'sports'} content` :
                selectedType === 'image' ? 'Perfect for player photos and team shots' : 
                selectedType === 'type' ? 'Great for announcements and stats' : 'Motion graphics and highlights'
              }
            </Text>
          </Box>
          {(dynamicGenerationEnabled || analysisData) && (
            <VStack spacing={1} align="end">
              <Badge colorScheme="green" variant="outline">AI-Enhanced</Badge>
              {analysisData && (
                <Text fontSize="xs" color="gray.500">
                  {generatedDesigns.filter(d => d.isRecommended).length} recommended
                </Text>
              )}
            </VStack>
          )}
        </Flex>

        {loading ? (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" color="blue.500" mb={4} />
            <Text color="gray.600">
              {dynamicGenerationEnabled ? 'Generating smart designs...' : 'Loading templates...'}
            </Text>
          </Box>
        ) : error ? (
          <Box textAlign="center" py={8} color="red.500">
            <Text>{error}</Text>
            <Button mt={2} onClick={() => fetchTemplatesForType(selectedType)} size="sm">
              Try Again
            </Button>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
            {generatedDesigns.map((design) => (
              <Box
                key={design.id}
                bg="white"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                border="2px"
                borderColor={design.isRecommended ? "green.300" : "gray.200"}
                _hover={{ 
                  transform: 'translateY(-4px)', 
                  boxShadow: 'xl',
                  borderColor: design.isRecommended ? "green.400" : "blue.300"
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                cursor="pointer"
                onClick={() => handleDesignSelect(design)}
                position="relative"
              >
                {/* AI Recommendation Badge */}
                {design.isRecommended && (
                  <Badge 
                    position="absolute" 
                    top={2} 
                    left={2} 
                    colorScheme="green" 
                    variant="solid"
                    zIndex={2}
                  >
                    AI Recommended
                  </Badge>
                )}
                
                <Box position="relative" overflow="hidden">
                  <Box
                    w="100%"
                    h="300px"
                    bgImage={`url(${design.thumbnail_url})`}
                    bgSize="contain"
                    bgRepeat="no-repeat"
                    bgPosition="center"
                    bg="gray.50"
                  />
                  
                  {/* Smart badges for future AI-generated designs */}
                  {design.isGenerated && (
                    <Badge 
                      position="absolute" 
                      top={2} 
                      right={2} 
                      colorScheme="purple" 
                      variant="solid"
                    >
                      AI Generated
                    </Badge>
                  )}
                  
                  <Box
                    position="absolute"
                    inset="0"
                    bg="blackAlpha.700"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    opacity="0"
                    _hover={{ opacity: 1 }}
                    transition="opacity 0.3s"
                  >
                    <VStack spacing={3}>
                      <Button
                        colorScheme={design.isRecommended ? "green" : "blue"}
                        size="lg"
                        leftIcon={<Text fontSize="16px">✨</Text>}
                        fontWeight="semibold"
                      >
                        Customize This Design
                      </Button>
                      <Text color="white" fontSize="sm" textAlign="center" px={4}>
                        {design.matchReason || `Optimized for ${selectedType === 'image' ? 'photos' : selectedType === 'type' ? 'text' : 'video'}`}
                      </Text>
                    </VStack>
                  </Box>
                </Box>
                
                <Box p={4}>
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1} flex="1">
                      <Text fontWeight="semibold" color="gray.800" fontSize="lg">
                        {design.name || 'Smart Design'}
                      </Text>
                      <HStack spacing={2}>
                        <Badge colorScheme="blue" size="sm">
                          {selectedType === 'image' ? 'Photo' : selectedType === 'type' ? 'Text' : 'Video'} Design
                        </Badge>
                        {design.smartScore > 0.7 ? (
                          <Badge colorScheme="green" size="sm">Perfect Match</Badge>
                        ) : (
                          <Badge colorScheme="gray" size="sm">Good Option</Badge>
                        )}
                      </HStack>
                      {analysisData && (
                        <Text fontSize="xs" color="gray.500">
                          Match: {Math.round(design.smartScore * 100)}%
                        </Text>
                      )}
                    </VStack>
                    <Button
                      colorScheme={design.isRecommended ? "green" : "blue"}
                      variant="ghost"
                      size="sm"
                      rightIcon={<Text fontSize="12px">→</Text>}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDesignSelect(design);
                      }}
                    >
                      Start
                    </Button>
                  </HStack>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}

        {generatedDesigns.length === 0 && !loading && !error && (
          <Box textAlign="center" py={8}>
            <Text color="gray.600" mb={4}>No designs available for this type yet.</Text>
            <Button onClick={() => setSelectedType('image')} size="sm">
              Try Photo Designs
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SmartDesignSelector; 